/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import {Blob} from '@google/genai';

function encode(bytes: Uint8Array) {
  let binary = '';
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function decode(base64: string) {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

/**
 * Float32 PCM [-1, 1] -> base64 little-endian Int16 PCM Blob for Gemini Live.
 * `sampleRate` must be the real rate of `data` (the Live API resamples any
 * declared rate; declaring the wrong one makes the audio unintelligible).
 */
function createBlob(data: Float32Array, sampleRate = 16000): Blob {
  const l = data.length;
  const int16 = new Int16Array(l);
  for (let i = 0; i < l; i++) {
    // Clamp first: 1.0 * 32768 overflows Int16 and wraps to -32768 (a click).
    const s = Math.max(-1, Math.min(1, data[i]));
    int16[i] = s < 0 ? s * 32768 : s * 32767;
  }

  return {
    data: encode(new Uint8Array(int16.buffer)),
    mimeType: `audio/pcm;rate=${Math.round(sampleRate)}`,
  };
}

async function decodeAudioData(
  data: Uint8Array,
  ctx: AudioContext,
  sampleRate: number,
  numChannels: number,
): Promise<AudioBuffer> {
  // Int16 needs an even byte count; drop a trailing odd byte rather than throw.
  const byteLength = data.byteLength - (data.byteLength % 2);
  const dataInt16 =
    data.byteOffset % 2 === 0
      ? new Int16Array(data.buffer, data.byteOffset, byteLength / 2)
      : new Int16Array(data.slice(0, byteLength).buffer);
  const buffer = ctx.createBuffer(
    numChannels,
    Math.max(1, Math.floor(dataInt16.length / numChannels)),
    sampleRate,
  );

  const l = dataInt16.length;
  const dataFloat32 = new Float32Array(l);
  for (let i = 0; i < l; i++) {
    dataFloat32[i] = dataInt16[i] / 32768.0;
  }
  // Extract interleaved channels
  if (numChannels === 0) {
    buffer.copyToChannel(dataFloat32, 0);
  } else {
    for (let i = 0; i < numChannels; i++) {
      const channel = dataFloat32.filter(
        (_, index) => index % numChannels === i,
      );
      buffer.copyToChannel(channel, i);
    }
  }

  return buffer;
}

export {createBlob, decode, decodeAudioData, encode};
